import type { FC } from 'react';
import { InlineEditableText } from '@components/InlineEditableText';
import InputMultiSelect from '@components/InputMultiSelect';
import InputSelect from '@components/InputSelect';
import Tooltip from '@components/Tooltip';
import { responsiveTruncateClasses } from '@ContractBuilder/contract-builder-header/classes';
import { EntityField } from '@ContractBuilder/contract-builder-header/constants';
import type { OnSaveFn } from '@ContractBuilder/contract-builder-header/modules/entity-details/hooks/use-editable-field';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import type { EntityData } from '@ContractBuilder/types';
import { UserGroupIcon } from '@heroicons/react/solid';
import { useTeamsForUser } from '@queries/fetchTeams';
import type { Team } from '@root/@types/types';
import { isTemplatePath } from '@utils/app-paths';

import { useEditableField } from '../hooks';

interface EditableTeamsFieldProps {
  entity: EntityData;
  hasOperationsDisabled: boolean;
  onSave: OnSaveFn<number[]>;
}

const getEntityTeamsDisplay = (userTeams: Team[], teams: number[]) => {
  const teamsLength = teams.length;
  const userTeamsLength = userTeams.length;
  if (teamsLength === 0 && userTeamsLength === 0) {
    return 'No team';
  }

  if (teamsLength === 0) {
    return 'All teams';
  }

  if (teamsLength === 1) {
    return userTeams.find((team) => team.id === teams[0])?.name ?? 'Team not found';
  }

  return `${teamsLength} teams`;
};

export const EditableTeamsField: FC<EditableTeamsFieldProps> = ({ entity, hasOperationsDisabled, onSave }) => {
  const hasUnsavedChanges = useDatapointsStore((state) => state.isDirty);
  const isDisabled = hasOperationsDisabled || hasUnsavedChanges;

  const { onCancel, onChange, onClick, onUpdate, state } = useEditableField<number[]>({
    defaultValue: entity.teams ?? [],
    name: EntityField.Teams,
    onSave,
  });

  const isTemplate = isTemplatePath();
  const { data: userTeams = [] } = useTeamsForUser();

  const handleChange = (value: number | number[] = []) => {
    onChange(Array.isArray(value) ? value : [value]);
  };

  return (
    <div className="flex items-center gap-1">
      <Tooltip content={`Team${isTemplate ? 's' : ''}`}>
        <span>
          <UserGroupIcon className="h-4 w-4" />
        </span>
      </Tooltip>
      <InlineEditableText
        editor={
          isTemplate ? (
            <InputMultiSelect
              dataTestId="multiselect:editable-teams"
              value={state?.value}
              options={userTeams.map((team) => ({ name: team.name, value: team.id }))}
              onChange={handleChange}
              onSelect={() => {}}
              name="teams"
              isDisabled={false}
              className="template-header"
              selectedItemClassName="-my-0 inline-block mb-[0.1rem]! pr-3"
              remainingItemsClassName="rounded-md bg-rounded py-1 -my-1 pl-3 pr-2 mr-1 text-gray-900 text-xs! font-normal bg-info-200! flex items-center last:mb-0"
              remainingTriggerClassName="!mt-0 !mr-1 !mb-[0.8px] !ml-0"
              placeholder="Select teams"
              size="sm"
            />
          ) : (
            <InputSelect
              dataTestId="select:editable-teams"
              value={state?.value?.[0]}
              options={userTeams.map((team) => ({ name: team.name, value: team.id }))}
              onChange={handleChange}
              name="teams"
              isDisabled={false}
              placeholder="Select team"
              size="sm"
              menuOuterClassName="min-w-fit"
            />
          )
        }
        editMode={state.isEditing}
        disabled={isDisabled}
        display={getEntityTeamsDisplay(userTeams, entity?.teams ?? [])}
        onClick={onClick}
        onCancel={onCancel}
        onConfirm={onUpdate}
        confirmDisabled={hasUnsavedChanges}
        tooltipContent={!isDisabled ? `Edit team${isTemplate ? 's' : ''}` : undefined}
        withEditIcon={false}
        fallbackAlert={false}
        className={responsiveTruncateClasses([
          'rounded-sm border border-transparent px-1 py-0.5',
          !isDisabled ? 'hover:border-gray-300' : '',
        ])}
      />
    </div>
  );
};
